import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { FileText, Layers, Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { EntityIndexHeader, EntityIndexLayout } from '@/components/layouts';
import {
  TemplateListPanel,
  type TemplateTypeFilter,
} from '@/components/organisms/templates/TemplateListPanel';
import { TemplateTableView } from '@/components/organisms/templates/TemplateTableView';
import { useHasCapability } from '@/hooks/usePermissions';
import { resolveTemplateLayoutMode } from '@/lib/templates/template-layout';

const TEMPLATE_TYPES: TemplateTypeFilter[] = ['all', 'estimate', 'proposal'];

interface TemplateRouteSearch {
  type?: TemplateTypeFilter;
  q?: string;
}

export const Route = createFileRoute('/_dashboard/dashboard/settings/templates')({
  validateSearch: (search: Record<string, unknown>): TemplateRouteSearch => ({
    type: TEMPLATE_TYPES.includes(search.type as TemplateTypeFilter)
      ? (search.type as TemplateTypeFilter)
      : 'all',
    q: typeof search.q === 'string' ? search.q : '',
  }),
  component: TemplatesLayout,
});

function cleanTemplateSearch(next: Record<string, unknown>) {
  if (next.q == null || next.q === '') delete next.q;
  if (next.type === 'all') delete next.type;
  return next as TemplateRouteSearch;
}

function buildTemplateChildSearch(type: TemplateTypeFilter, q: string) {
  return {
    tab: 'overview' as const,
    ...cleanTemplateSearch({ type, q }),
  };
}

function TemplatesLayout() {
  const router = useRouter();
  const navigate = Route.useNavigate();
  const { location } = useRouterState();
  const filters = Route.useSearch();
  const typeFilter = filters.type ?? 'all';
  const query = filters.q ?? '';
  const canManageTemplates = useHasCapability('can_manage_templates');

  const idMatch = location.pathname.match(
    /\/dashboard\/settings\/templates\/(?:estimate|proposal)\/([^/]+)/,
  );
  const selectedId = idMatch?.[1] !== 'new' ? (idMatch?.[1] ?? undefined) : undefined;
  const { isFormMode, hideListInSplit } = resolveTemplateLayoutMode(
    location.pathname,
    (location.search as { mode?: string }).mode,
  );
  const hasChildRoute =
    location.pathname.includes('/templates/estimate/') ||
    location.pathname.includes('/templates/proposal/');

  function updateFilters(patch: Partial<TemplateRouteSearch>) {
    void navigate({
      search: (prev) => cleanTemplateSearch({ ...prev, ...patch }),
      replace: true,
    });
  }

  function handleTypeFilterChange(type: TemplateTypeFilter) {
    updateFilters({ type });
  }

  const newButton = (openInSheet?: (navigate: () => void) => void) =>
    canManageTemplates ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon-sm" title="New">
            <Plus className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              if (openInSheet) {
                openInSheet(
                  () => void router.navigate({ to: '/dashboard/settings/templates/estimate/new' }),
                );
              } else {
                void router.navigate({ to: '/dashboard/settings/templates/estimate/new' });
              }
            }}
          >
            <FileText className="mr-2 h-4 w-4" />
            Estimate Template
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              if (openInSheet) {
                openInSheet(
                  () => void router.navigate({ to: '/dashboard/settings/templates/proposal/new' }),
                );
              } else {
                void router.navigate({ to: '/dashboard/settings/templates/proposal/new' });
              }
            }}
          >
            <Layers className="mr-2 h-4 w-4" />
            Proposal Template
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;

  return (
    <EntityIndexLayout
      title="Templates"
      storageKey="templates-view"
      hasChildRoute={hasChildRoute}
      onResetToIndex={() =>
        void router.navigate({
          to: '/dashboard/settings/templates',
          search: () => cleanTemplateSearch({ type: typeFilter, q: query }),
          replace: true,
        })
      }
      splitWidthClass="w-[360px]"
      tableSheetWidthClass={
        isFormMode ? 'w-[95vw] sm:max-w-[1200px]' : 'w-full sm:w-[620px] sm:max-w-[620px]'
      }
      hideListInSplit={hideListInSplit}
      splitList={({ viewToggle }) => (
        <div className="flex h-full flex-col">
          <EntityIndexHeader
            title="Templates"
            actions={
              <>
                {viewToggle}
                {newButton()}
              </>
            }
          />
          <TemplateListPanel
            selectedId={selectedId}
            typeFilter={typeFilter}
            onTypeFilterChange={handleTypeFilterChange}
            search={query}
            onSearchChange={(q) => updateFilters({ q })}
            autoSelectFirst={!hasChildRoute}
          />
        </div>
      )}
      splitDetail={<Outlet />}
      tableView={({ viewToggle, openInSheet }) => (
        <>
          <EntityIndexHeader
            title="Templates"
            actions={
              <>
                {viewToggle}
                {newButton(openInSheet)}
              </>
            }
          />
          <TemplateTableView
            search={query}
            onSearchChange={(q) => updateFilters({ q })}
            onOpen={(kind, id) =>
              openInSheet(() => {
                if (kind === 'estimate') {
                  void router.navigate({
                    to: '/dashboard/settings/templates/estimate/$id',
                    params: { id },
                    search: () => buildTemplateChildSearch(typeFilter, query),
                  });
                } else {
                  void router.navigate({
                    to: '/dashboard/settings/templates/proposal/$id',
                    params: { id },
                    search: () => buildTemplateChildSearch(typeFilter, query),
                  });
                }
              })
            }
          />
        </>
      )}
      sheetContent={<Outlet />}
      mobileContent={
        <div className="md:hidden" style={{ height: 'calc(100dvh - 5rem)' }}>
          <Outlet />
        </div>
      }
    />
  );
}
